<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import { useTerritoriosStore } from "@/store/territorios";
import { useNominatim } from "@/composables/useNominatim";
import MapaBase from "@/components/MapaBase/MapaBase.vue";
import { useScreenStore } from "@/store/useScreenStore";
import { useAppToast } from "@/composables/useAppToast";

const toast = useAppToast();
const screen = useScreenStore();
const territorios = useTerritoriosStore();
const { reverseGeocode } = useNominatim();

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const visible = ref(false);

const form = ref({
  name: "",
  direccion: "",
  latlng: { lat: 0, lng: 0 },
});

watch(
  () => props.editItem,
  (item) => {
    if (item) {
      form.value = JSON.parse(JSON.stringify(item));
      visible.value = true;
    }
  }
);

const click = async (e) => {
  form.value.latlng = e.latlng;
  // Autocompletar la dirección por reverse geocoding
  try {
    const address = await reverseGeocode(e.latlng);
    const a = address?.address;
    form.value.direccion =
      (a?.house_number ? `${a.road} ${a.house_number}`.trim() : null) ??
      address?.intersection ??
      a?.road ??
      address?.name ??
      address?.display_name?.split(",")[0] ??
      "";
  } catch (err) {
    console.error("Error en reverse geocoding:", err);
  }
};

const save = () => {
  const val = JSON.parse(JSON.stringify(form.value));
  // La dirección es obligatoria; el nombre es opcional
  val.direccion = val.direccion?.trim() || "";
  if (!val.direccion) {
    toast.warn("Falta la dirección", "Ingrese la dirección del punto de encuentro.");
    return;
  }
  val.name = val.name?.trim() || val.direccion;
  if (!val.id) val.id = crypto.randomUUID();

  if (props.editItem) {
    territorios.$patch({
      puntoEncuentro: territorios.puntoEncuentro.map((p) =>
        p.id === val.id ? val : p
      ),
    });
  } else {
    territorios.$patch({
      puntoEncuentro: [...territorios.puntoEncuentro, val],
    });
  }
  form.value = { name: "", direccion: "", latlng: { lat: 0, lng: 0 } };
  visible.value = false;
  emit("saved");
};
</script>
<template>
  <div>
    <Button label="Agregar Punto de Encuentro" @click="visible = true" />
    <Dialog
      v-model:visible="visible"
      modal
      :style="{ width: screen.isMobile ? '100%' : '50%' }"
    >
      <div class="flex flex-column gap-2">
        <div>
          <label class="pb-1">Dirección</label>
          <InputText v-model="form.direccion" placeholder="Ej: Av. Corrientes 1234" autofocus class="w-full" />
          <small class="text-500">Se completa automáticamente al marcar en el mapa.</small>
        </div>
        <div>
          <label class="pb-1">Nombre <span class="text-xs text-500">(opcional)</span></label>
          <InputText v-model="form.name" placeholder="Ej: Salón del Reino" class="w-full" />
        </div>
        <div class="text-xs text-500">
          Coordenadas: {{ form.latlng.lat }}, {{ form.latlng.lng }}
        </div>
      </div>
      <div class="pt-3">
        <MapaBase
          @mapClick="click"
          class="map"
          :setPoint="true"
          style="height: 50vh;"
        />
      </div>
      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          @click="visible = false"
          text
        />
        <Button label="Guardar" icon="pi pi-check" @click="save()" />
      </template>
    </Dialog>
  </div>
</template>
